import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminListAnnouncements, adminCreateAnnouncement, adminUpdateAnnouncement, adminDeleteAnnouncement, adminSendNotification,
} from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import NotificationRules from "@/components/admin/NotificationRules";
import { Plus, Loader2, Save, X, Trash2, Eye, EyeOff, Megaphone, Send } from "lucide-react";

const blank = { title: "", body: "", image_url: "", link: "", active: true };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState({ title: "", body: "", link: "", target_all: true, interests: "", regions: "", deities: "", goal_contains: "" });
  const [sending, setSending] = useState(false);

  const load = () => { setLoading(true); adminListAnnouncements().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast("A title is required"); return; }
    setBusy(true);
    try {
      if (editing === "new") await adminCreateAnnouncement(form);
      else await adminUpdateAnnouncement(editing.id, form);
      toast("Saved"); setEditing(null); load();
    } catch (err) { toast("Could not save", { description: err?.response?.data?.detail || "Try again." }); }
    finally { setBusy(false); }
  };
  const del = async (a) => { if (!window.confirm("Delete this announcement?")) return; await adminDeleteAnnouncement(a.id); setItems((x) => x.filter((y) => y.id !== a.id)); };
  const toggle = async (a) => { const u = await adminUpdateAnnouncement(a.id, { active: !a.active }); setItems((x) => x.map((y) => y.id === a.id ? u : y)); };

  const send = async (e) => {
    e.preventDefault();
    if (!notify.title.trim()) { toast("A notification title is required"); return; }
    setSending(true);
    try {
      const payload = {
        title: notify.title, body: notify.body, link: notify.link, target_all: notify.target_all,
        interests: notify.interests.split(",").map((s) => s.trim()).filter(Boolean),
        regions: notify.regions.split(",").map((s) => s.trim()).filter(Boolean),
        deities: notify.deities.split(",").map((s) => s.trim()).filter(Boolean),
        goal_contains: notify.goal_contains.trim() || null,
      };
      const r = await adminSendNotification(payload);
      toast(`Sent to ${r.sent} sadhak${r.sent === 1 ? "" : "s"}`);
      setNotify({ title: "", body: "", link: "", target_all: true, interests: "", regions: "", deities: "", goal_contains: "" });
    } catch (err) { toast("Could not send", { description: err?.response?.data?.detail || "Try again." }); }
    finally { setSending(false); }
  };

  const field = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">Announcements & Notifications</h1>
          <p className="mt-1 text-sm text-ink-3">Post homepage announcements and send targeted in-app notifications.</p>
        </div>
        <button onClick={() => { setForm(blank); setEditing("new"); }} data-testid="ann-new" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> New announcement</button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="ann-form" className="mt-6 border border-line bg-surface p-5">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" placeholder="Announcement title" className={field} />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} placeholder="Body (optional)" className={`${field} mt-3`} />
          <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link (optional, e.g. /events/…)" className={`${field} mt-3`} />
          <div className="mt-3"><ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
          <label className="mt-3 flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Show on homepage</label>
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={busy} data-testid="ann-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
            <button type="button" onClick={() => setEditing(null)} className="border border-line px-4 py-2 text-sm text-ink-2"><X className="h-4 w-4" /></button>
          </div>
        </form>
      )}

      <div className="mt-6 divide-y divide-line border-y border-line">
        {loading ? <div className="flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : items.length === 0 ? <p className="py-10 text-center text-ink-3">No announcements yet.</p>
          : items.map((a) => (
            <div key={a.id} data-testid={`admin-ann-${a.id}`} className={`flex items-center gap-4 py-4 ${a.active ? "" : "opacity-50"}`}>
              <Megaphone className="h-4 w-4 shrink-0 text-ink-3" />
              <div className="min-w-0 flex-1"><p className="font-serif-latin text-ink">{a.title}</p><p className="line-clamp-1 text-xs text-ink-3">{a.body}{a.active ? "" : " · HIDDEN"}</p></div>
              <button onClick={() => { setForm({ ...blank, ...a }); setEditing(a); }} className="p-2 text-ink-2 hover:text-saffron" title="Edit"><Save className="h-4 w-4" /></button>
              <button onClick={() => toggle(a)} className="p-2 text-ink-2 hover:text-saffron">{a.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              <button onClick={() => del(a)} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
      </div>

      {/* Targeted notification */}
      <div className="mt-10 border border-line bg-surface p-5">
        <h2 className="inline-flex items-center gap-2 font-serif-latin text-xl text-ink"><Send className="h-4 w-4 text-saffron" /> Send a notification</h2>
        <p className="mt-1 text-sm text-ink-3">Delivered to the in-app bell of the sadhaks you target.</p>
        <form onSubmit={send} data-testid="notify-form" className="mt-4">
          <input value={notify.title} onChange={(e) => setNotify({ ...notify, title: e.target.value })} data-testid="notify-title" placeholder="Notification message" className={field} />
          <input value={notify.link} onChange={(e) => setNotify({ ...notify, link: e.target.value })} placeholder="Link (optional, e.g. /events)" className={`${field} mt-3`} />
          <label className="mt-3 flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={notify.target_all} onChange={(e) => setNotify({ ...notify, target_all: e.target.checked })} data-testid="notify-all" /> Send to everyone</label>
          {!notify.target_all && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input value={notify.interests} onChange={(e) => setNotify({ ...notify, interests: e.target.value })} data-testid="notify-interests" placeholder="Interests (comma sep): Bhakti, Jyotisha" className={field} />
              <input value={notify.regions} onChange={(e) => setNotify({ ...notify, regions: e.target.value })} data-testid="notify-cities" placeholder="Cities (comma sep): Varanasi, Delhi" className={field} />
              <input value={notify.deities} onChange={(e) => setNotify({ ...notify, deities: e.target.value })} data-testid="notify-deities" placeholder="Deities (comma sep): Shiva, Devi" className={field} />
              <input value={notify.goal_contains} onChange={(e) => setNotify({ ...notify, goal_contains: e.target.value })} data-testid="notify-goal" placeholder="Goal contains (text)" className={field} />
            </div>
          )}
          <button type="submit" disabled={sending} data-testid="notify-send" className="mt-4 inline-flex items-center gap-1.5 bg-[var(--ink)] px-5 py-2.5 text-sm font-medium text-[var(--surface)] disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send notification
          </button>
        </form>
      </div>

      <NotificationRules />
    </div>
  );
}
